import React, {
  type ChangeEventHandler,
  type KeyboardEventHandler,
  useRef,
  useState,
} from "react";
import { BaseInputV2 } from "src/components/BaseInputV2/BaseInputV2.tsx";
import {
  autoUpdate,
  flip,
  FloatingOverlay,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { useControlled } from "@exonn/hooks";
import type {
  AutocompleteProps,
  AutocompleteOption,
} from "src/components/Autocomplete/Autocomplete.types.ts";

import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useTranslation } from "react-i18next";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import dayjs from "dayjs";
import { InputErrorMessage } from "src/components/InputErrorMessage/InputErrorMessage.tsx";
import { DateField } from "src/components/DateField/DateField.tsx";
import { AnimatedLogo } from "src/components/AnimatedLogo/AnimatedLogo.tsx";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

await loadTranslations(TranslationNamespaceEnum.AutocompleteComponent);

export const Autocomplete = <Meta = any,>(props: AutocompleteProps<Meta>) => {
  const {
    error,
    placeholder,
    searchValue: searchValueProp,
    onSearchValueChange,
    onValueChange,
    defaultSearchValue = "",
    isLoading,
    floatingError = false,
    label,
    options = [],
    defaultValue: defaultValueProp = "",
    value: valueProp,
    isError,
    showSkeleton = false,
    isOpen: isOpenProp,
    setIsOpen: setIsOpenProp,
    startSearchWith = 2,
    type = "default",
    style,
    ...restProps
  } = props;

  const [isOpen, setIsOpen] = useControlled({
    name: "Autocomplete",
    state: "isOpen",
    controlled: isOpenProp,
    default: !!isOpenProp,
    onChange: setIsOpenProp,
  });

  const { refs, context, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: 7,
      }),
      flip(),
      size({
        apply({ rects, elements, availableHeight }) {
          const maxHeight = availableHeight - 10;
          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeight}px`,
            maxWidth: `${rects.reference.width}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const [value, setValue] = useControlled({
    name: "Autocomplete",
    state: "activeValue",
    controlled: valueProp,
    default: defaultValueProp,
  });

  const listRef = useRef<Array<HTMLElement | null>>([
    ...Array(options.length).fill(null),
  ]);
  const [searchValue, setSearchValue] = useControlled({
    name: "Autocomplete",
    state: "searchValue",
    controlled: searchValueProp,
    default: defaultSearchValue,
    onChange: onSearchValueChange,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);

  const [activeIndex, setActiveIndex] = useState<number | null>(selectedIndex);

  const handleNavigate = (index: number | null) => {
    if (index == null) {
      inputRef.current?.focus();
    }
    // for some reason index can be string
    const parsedIndex = index != null ? +index : index;
    setActiveIndex(parsedIndex);
  };

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: searchValue.length >= startSearchWith }),
    useDismiss(context),
    useListNavigation(context, {
      listRef,
      activeIndex,
      allowEscape: true,
      selectedIndex,
      focusItemOnOpen: false,
      focusItemOnHover: true,
      openOnArrowKeyDown: false,
      loop: true,
      onNavigate: handleNavigate,
    }),
  ]);

  const select = (option: AutocompleteOption) => {
    setIsOpen(false);
    setValue(option?.value);
    onValueChange?.(option?.value, option?.meta);
  };
  const onSearch = (s: string) => {
    setSearchValue(s);
  };

  const renderOption = (option: AutocompleteOption) => {
    return option.renderOption ? option.renderOption(option) : option.label;
  };

  const handleInputKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(0);
    }
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onSearch(e.currentTarget.value);
  };

  const handleFloatingKeyDown: KeyboardEventHandler = (e) => {
    //   setPointer(false);
    if (e.key === "Enter" && activeIndex !== null) {
      select(options[activeIndex]);
    }
    //   if (e.key === " " && !isTypingRef.current) {
    //   e.preventDefault();
    // }
  };

  const handleItemKeyDown =
    (index: number): KeyboardEventHandler =>
    (e) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (index === 0) {
          e.stopPropagation();
          setActiveIndex(null);
          inputRef.current?.focus();
        }
      }
    };

  return (
    <Skeleton isLoading={showSkeleton}>
      <div style={style} {...restProps}>
        {label && <label css={labelStyles}>{label}</label>}
        {type === "date" ? (
          <div ref={refs.setReference}>
            <DateField
              error={error}
              value={searchValue ? dayjs(searchValue) : null}
              onChange={(value) => {
                onSearch(value ? value.format("YYYY-MM-DD") : "");
              }}
            />
          </div>
        ) : (
          <div style={{ position: "relative" }} ref={refs.setReference}>
            <BaseInputV2
              ref={inputRef}
              hasError={!!error}
              autoComplete={"new-off"}
              placeholder={placeholder}
              {...getReferenceProps({
                onKeyDown: handleInputKeyDown,
                onChange: handleInputChange,
              })}
              value={searchValue + ""}
            />
            {!!error && (
              <InputErrorMessage floating={floatingError} message={error} />
            )}
          </div>
        )}

        {searchValue.length >= startSearchWith && isOpen && (
          <FloatingOverlay style={{ zIndex: 5555 }}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps({
                onKeyDown: handleFloatingKeyDown,
              })}
              css={container}
              tabIndex={-1}
              style={floatingStyles}
            >
              {isError ? (
                <ErrorState />
              ) : isLoading ? (
                <LoadingState />
              ) : options.length === 0 ? (
                <EmptyState />
              ) : (
                options.map((option, index) => {
                  return (
                    <div
                      ref={(node) => (listRef.current[index] = node)}
                      tabIndex={activeIndex === index ? 0 : -1}
                      key={option.value}
                      onKeyDown={handleItemKeyDown(index)}
                      css={item({
                        isSelected: selectedIndex === index,
                        isActive: activeIndex === index,
                      })}
                      onClick={() => select(option)}
                    >
                      {renderOption(option)}
                    </div>
                  );
                })
              )}
            </div>
          </FloatingOverlay>
        )}
      </div>
    </Skeleton>
  );
};

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div css={noOptionsState}>
      <i className={"icon-15-no_found"} />
      <div css={stateTextContainer}>
        {t("no_options", {
          ns: TranslationNamespaceEnum.AutocompleteComponent,
        })}
      </div>
    </div>
  );
};

const ErrorState = () => {
  const { t } = useTranslation();
  return (
    <div css={errorState}>
      <i className={"icon-15-error"} />
      <div css={stateTextContainer}>
        {t("error", {
          ns: TranslationNamespaceEnum.AutocompleteComponent,
        })}
      </div>
    </div>
  );
};

const LoadingState = () => {
  return (
    <div css={autocompleteState}>
      <AnimatedLogo css={logo} />
    </div>
  );
};

const item =
  ({ isSelected, isActive }: { isSelected: boolean; isActive: boolean }) =>
  (theme: ExactTheme) => css`
    min-height: 35px;
    display: flex;
    flex-direction: column;
    outline: none;
    align-items: flex-start;
    padding: 10px;
    border-bottom: 1px solid ${theme.colorsV2.grey[100]};
    cursor: pointer;
    background-color: ${isSelected || isActive
      ? theme.colorsV2.grey[50]
      : theme.colorsV2.white};
    :last-of-type {
      border-bottom: unset;
    }
    :hover {
      background-color: ${theme.colorsV2.grey[50]};
    }
    width: auto;
  `;

const labelStyles = (theme: ExactTheme) => css`
  font-size: ${theme.fontSizesV2.body};
  color: ${theme.colorsV2.grey[800]};
  font-weight: ${theme.fontWeights.normal};
  line-height: 21px;
`;

const container = (theme: ExactTheme) => css`
  border-radius: 6px;
  overflow-y: auto;
  width: 100%;
  background: white;
  box-shadow: ${theme.shadowV2.secondary};
  border: 1px solid ${theme.colorsV2.grey[200]};
  min-height: 35px;
`;

const autocompleteState = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 10px 0;
`;

const errorState = (theme: ExactTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 15px;
  color: ${theme.colorsV2.red[500]};
  flex-direction: column;
  padding: 10px 0;
`;

const logo = css`
  height: 30px;
  width: 32px;
`;

const noOptionsState = (theme: ExactTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 15px;
  flex-direction: column;
  padding: 10px 0;
  color: ${theme.colorsV2.blue[500]};
  text-align: center;
`;

const stateTextContainer = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.grey[800]};
  margin-top: 5px;
`;
